import { useEffect, useState, type Dispatch, type SetStateAction } from 'react'
import { useSesion } from '../store/sesion'

/** Lo que alguien está llenando y todavía no guarda —un carro de venta a
 *  medio armar, una orden de ingreso con tres productos ya cargados— no
 *  se pierde al ir a otra pantalla y volver.
 *
 *  Antes cada página guardaba eso en su propio `useState`, que muere
 *  apenas la página se desmonta: ir a Inventario a mirar el stock de
 *  frambuesas en medio de un ingreso obligaba a empezarlo de cero.
 *
 *  Va a `localStorage` y no a `sessionStorage` a propósito: en el
 *  teléfono, cambiar a WhatsApp y volver puede hacer que el sistema cierre
 *  la app por detrás, y `sessionStorage` se va con ella.
 *
 *  Cada borrador es de la persona que lo empezó (hay teléfonos que se
 *  pasan entre vendedores con "Cambiar de vendedor") y vence a las 12
 *  horas: un carro olvidado de ayer no debe aparecer precargado hoy. */

const PREFIJO = 'nb-borrador'
const VIGENCIA_MS = 12 * 60 * 60 * 1000

interface Guardado<T> {
  v: T
  t: number
}

const usuarioActualId = () => useSesion.getState().usuario?.id ?? 'sin-sesion'
const claveDe = (usuario: string, nombre: string) => `${PREFIJO}:${usuario}:${nombre}`

function leer<T>(clave: string): T | undefined {
  try {
    const crudo = localStorage.getItem(clave)
    if (!crudo) return undefined
    const guardado = JSON.parse(crudo) as Guardado<T>
    if (Date.now() - guardado.t > VIGENCIA_MS) {
      localStorage.removeItem(clave)
      return undefined
    }
    return guardado.v
  } catch {
    // Almacenamiento bloqueado (modo privado) o dato corrupto: se parte
    // de cero, que es exactamente lo que pasaba antes de esto.
    return undefined
  }
}

/** Igual que `useState`, pero lo que se escribe sobrevive a salir de la
 *  pantalla. `nombre` va con el grupo adelante ("venta.carro",
 *  "ingreso.proveedor") para poder descartar todo el grupo de una vez. */
export function useBorrador<T>(
  nombre: string,
  inicial: T | (() => T),
): [T, Dispatch<SetStateAction<T>>] {
  const usuario = useSesion((s) => s.usuario?.id ?? 'sin-sesion')
  const clave = claveDe(usuario, nombre)
  const [valor, setValor] = useState<T>(() => {
    const guardado = leer<T>(clave)
    if (guardado !== undefined) return guardado
    return typeof inicial === 'function' ? (inicial as () => T)() : inicial
  })

  useEffect(() => {
    try {
      localStorage.setItem(clave, JSON.stringify({ v: valor, t: Date.now() } satisfies Guardado<T>))
    } catch {
      // Sin espacio o bloqueado: el formulario sigue funcionando igual,
      // solo que sin la red de seguridad.
    }
  }, [clave, valor])

  return [valor, setValor]
}

/** Si hay algo guardado para ese nombre (y todavía vigente). */
export function hayBorrador(nombre: string): boolean {
  return leer(claveDe(usuarioActualId(), nombre)) !== undefined
}

/** Borra todos los campos de un grupo ("venta", "ingreso") de la persona
 *  actual. Se llama al guardar con éxito, para que la próxima vez el
 *  formulario parta limpio. */
export function descartarBorrador(grupo: string) {
  const prefijo = claveDe(usuarioActualId(), `${grupo}.`)
  try {
    for (const clave of Object.keys(localStorage)) {
      if (clave.startsWith(prefijo)) localStorage.removeItem(clave)
    }
  } catch {
    // Ver `leer`.
  }
}
